import type { PopulatedClaim } from '~/Utils/populateClaim';

import type {
  AnyClaimResponseBaseSchema,
  AutoClaim,
  AutoClaimForFe,
  DocumentMetaData,
  GeneralClaim,
  GeneralClaimForFe,
  GLClaim,
  HomeClaim,
  InvolvedNonMotoristBase,
  InvolvedPerson,
  PetClaim,
  UploadingDocument,
  WCClaim,
} from '../../server_shared/swagger/data-contracts';

import type { OrganizationModel } from './organization-types';

// Swagger doesnt deal well with polymorph so we construct these manually here

// based on claim_schema_mapping = {
//     AutoClaim: auto_claim_model,
//     GeneralClaim: general_claim_model,
// }
// in FE we have a polymorphic type for claim_for_fe, so we need to construct this manually here
export type ClaimForFePolymorph = (AutoClaimForFe | GeneralClaimForFe) & AnyClaimResponseBaseSchema;

export type EfficientClaimResponseSchemaWithPolymorph = ClaimForFePolymorph & {
  documents: DocumentMetaData[];
  uploading_documents?: UploadingDocument[];
};

// based on claim_schema_mapping = {
//     HomeClaim: home_claim_model,
//     AutoClaim: auto_claim_model,
//     GLClaim: gl_claim_model,
//     WCClaim: wc_claim_model,
//     PetClaim: pet_claim_model,
//     GeneralClaim: general_claim_model,
// }
// in server/api/schemas/claim_polymorph_schemas.py we have a polymorphic type for claim, so we need to construct this manually here
export type LegacyClaimPolymorph = (AutoClaim | PetClaim | HomeClaim | GLClaim | WCClaim | GeneralClaim) &
  AnyClaimResponseBaseSchema;

export type ClaimPolymorph = LegacyClaimPolymorph | EfficientClaimResponseSchemaWithPolymorph;

/**
 * Type guard to check if an involved person is InvolvedNonMotoristBase
 */
export function isInvolvedNonMotorist(
  involvedPerson: InvolvedPerson | undefined
): involvedPerson is InvolvedNonMotoristBase {
  return (
    involvedPerson !== undefined &&
    involvedPerson.type === 'involved_non_motorist' &&
    'non_motorist_type' in involvedPerson
  );
}

/**
 * Type guard to assert an incident object includes optional incident_type_desc
 */
export interface IncidentWithTypeDesc {
  incident_type_desc?: string;
}

export function incidentHasTypeDesc(incident: unknown): incident is IncidentWithTypeDesc {
  return (
    typeof incident === 'object' && incident !== null && 'incident_type_desc' in (incident as Record<string, unknown>)
  );
}

/**
 * Type guard to assert an incident configuration object includes optional incident_details or fnol
 */
export interface IncidentConfigurationPartial {
  incident_details?: {
    pre_defined_fields?: Record<string, unknown>;
  };
  fnol?: Record<string, unknown>;
}

export function isIncidentConfiguration(
  incidentConfiguration: unknown
): incidentConfiguration is IncidentConfigurationPartial {
  if (typeof incidentConfiguration === 'object' && incidentConfiguration !== null) {
    return 'incident_details' in incidentConfiguration || 'fnol' in incidentConfiguration;
  }
  return false;
}

// Extended PopulatedClaim to include organization property that gets added at runtime

export type PopulatedClaimWithOrganization = PopulatedClaim & {
  organization?: OrganizationModel | undefined; // This gets set from userOrganization
};
